import { type ReactNode, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/shared/cn";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { PanelToggle } from "@/shared/ui/primitives/PanelToggle";

export interface RightPanelContent {
  title: string;
  actions?: ReactNode;
  body: ReactNode;
}

export function RightPanel({
  panel,
  fullScreen,
  onToggleFullScreen,
  onClose,
}: {
  panel: RightPanelContent;
  fullScreen: boolean;
  onToggleFullScreen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [bodyHost] = useState(createBodyHost);
  const placeBody = useCallback(
    (slot: HTMLElement | null) => {
      slot?.appendChild(bodyHost);
    },
    [bodyHost],
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented || escapeBelongsElsewhere(event)) return;
      if (fullScreen) onToggleFullScreen();
      else onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullScreen, onToggleFullScreen, onClose]);

  const content = (
    <aside
      aria-label={panel.title}
      data-right-panel={fullScreen ? "full" : "docked"}
      className={cn(
        "flex min-h-0 flex-col bg-(--paper)",
        fullScreen
          ? "fixed inset-0 z-[180]"
          : "max-h-full overflow-hidden rounded-(--card-r) border border-(--line) shadow-none",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-start justify-between gap-[12px]",
          fullScreen
            ? "border-b border-(--line) pt-[20px] pr-[16px] pb-[16px] pl-[28px]"
            : "pt-[20px] pr-[16px] pb-[8px] pl-[22px]",
        )}
      >
        <h3 className="m-0 min-w-0 text-lg font-medium tracking-[-0.02em] text-(--ink)">
          {panel.title}
        </h3>
        <div className="flex shrink-0 items-center gap-[6px]">
          {panel.actions}
          <PanelToggle
            label={fullScreen ? t("shell.panel.exitFullScreen") : t("shell.panel.fullScreen")}
            onClick={onToggleFullScreen}
          >
            {fullScreen ? <Minimize2 aria-hidden /> : <Maximize2 aria-hidden />}
          </PanelToggle>
          <PanelToggle label={t("shell.panel.close")} onClick={onClose}>
            <X aria-hidden />
          </PanelToggle>
        </div>
      </div>
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto overscroll-contain",
          fullScreen
            ? "mx-auto w-full max-w-[1100px] px-[28px] py-[24px]"
            : "px-[22px] pt-[6px] pb-[24px]",
        )}
      >
        <div ref={placeBody} className="contents" />
      </div>
    </aside>
  );
  return (
    <>
      {fullScreen ? createPortal(content, document.body) : content}
      {createPortal(panel.body, bodyHost)}
    </>
  );
}

function createBodyHost(): HTMLElement {
  const host = document.createElement("div");
  host.style.display = "contents";
  return host;
}

function escapeBelongsElsewhere(event: KeyboardEvent): boolean {
  const target = event.target;
  if (target instanceof Element && target.closest(EDITABLE)) return true;
  return document.querySelector(OPEN_OVERLAY) !== null;
}

const EDITABLE = "input, textarea, select, [contenteditable='true']";

const OPEN_OVERLAY = "[role='menu'], [role='dialog'], [role='alertdialog'], [role='listbox']";
